import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Home from "@/pages/Home";
import Discover from "@/pages/Discover";
import Events from "@/pages/Events";
import CompanyProfile from "@/pages/CompanyProfile";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";
import Sign from "@/pages/Sign";
import Pay from "@/pages/Pay";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AppShell from "@/pages/app/AppShell";
import Overview from "@/pages/app/Overview";
import Pipeline from "@/pages/app/Pipeline";
import MyEvents from "@/pages/app/MyEvents";
import Agreements from "@/pages/app/Agreements";
import Payments from "@/pages/app/Payments";
import Messages from "@/pages/app/Messages";
import Calendar from "@/pages/app/Calendar";
import ProfileEdit from "@/pages/app/ProfileEdit";
import Network from "@/pages/app/Network";
import Alerts from "@/pages/app/Alerts";
import Briefs from "@/pages/app/Briefs";
import Inventory from "@/pages/app/Inventory";
import Wishlist from "@/pages/app/Wishlist";
import NewsAdmin from "@/pages/app/NewsAdmin";
import HQ from "@/pages/app/HQ";
import Promote from "@/pages/app/Promote";
import { PageTracker } from "@/lib/track";
import DataRoom from "@/pages/app/DataRoom";
import { DealRoomList, DealRoom } from "@/pages/app/DealRooms";
import { Team, Approvals } from "@/pages/app/TeamApprovals";
import Templates from "@/pages/app/Templates";
import PartnershipMap from "@/pages/PartnershipMap";
import Awards from "@/pages/Awards";
import AwardsCeremony from "@/pages/AwardsCeremony";
import Brief from "@/pages/Brief";
import OpportunityCalendar from "@/pages/OpportunityCalendar";
import News, { NewsArticle } from "@/pages/News";
import Valuation from "@/pages/Valuation";
import { About, HowItWorksPage, FAQ, Contact, Privacy, Terms } from "@/pages/InfoPages";

function SmoothScroll() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/app")) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [pathname]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-300" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
}

export default function App() {
  return (
    <div className="min-h-screen bg-[#FFF7FB]">
      <AuthProvider>
        <BrowserRouter>
          <SmoothScroll />
          <PageTracker />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/events" element={<Events />} />
            <Route path="/companies/:id" element={<CompanyProfile />} />
            <Route path="/briefs/:id" element={<Brief />} />
            <Route path="/opportunity-calendar" element={<OpportunityCalendar />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsArticle />} />
            <Route path="/valuation" element={<Valuation />} />
            <Route path="/about" element={<About />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/map" element={<PartnershipMap />} />
            <Route path="/awards" element={<Awards />} />
            <Route path="/awards/:year/ceremony" element={<AwardsCeremony />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/sign/:token" element={<Sign />} />
            <Route path="/pay/:token" element={<Pay />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<Overview />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="events" element={<MyEvents />} />
              <Route path="network" element={<Network />} />
              <Route path="messages" element={<Messages />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="agreements" element={<Agreements />} />
              <Route path="payments" element={<Payments />} />
              <Route path="profile" element={<ProfileEdit />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="briefs" element={<Briefs />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="wishlist" element={<Wishlist />} />
              <Route path="newsroom" element={<NewsAdmin />} />
              <Route path="hq" element={<HQ />} />
              <Route path="promote" element={<Promote />} />
              <Route path="dataroom" element={<DataRoom />} />
              <Route path="deal-rooms" element={<DealRoomList />} />
              <Route path="deal-rooms/:id" element={<DealRoom />} />
              <Route path="team" element={<Team />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="templates" element={<Templates />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-center" />
      </AuthProvider>
    </div>
  );
}
